import type { FAMI } from "@/models/fami";
import type { CoreDataModel, DropDownModel } from "@/models";

const lookupText = (
  list: DropDownModel[] | undefined,
  code: string | null | undefined
): string => {
  if (!code) return "—";
  return list?.find((x) => x.Value === code)?.Text || code;
};

export function getGenderText(code: string | null | undefined): string {
  if (!code) return "—";
  switch (code.toUpperCase()) {
    case "M":
      return "Male";
    case "F":
      return "Female";
    case "X":
      return "Nonbinary";
    default:
      return code;
  }
}

export function getHispanicIndicatorText(
  childEthnicity: string | boolean | null | undefined,
  childEthnicityMissingIndicator?: string | boolean | null
): string {
  const isMissing =
    childEthnicityMissingIndicator === true ||
    childEthnicityMissingIndicator === "Y";
  if (isMissing) return "No Response";
  if (childEthnicity === "" || childEthnicity == null) return "No Response";
  if (typeof childEthnicity === "boolean") {
    return childEthnicity ? "Hispanic" : "Not Hispanic";
  }
  return childEthnicity === "Y" ? "Hispanic" : "Not Hispanic";
}

export function getRaceText(
  codes: Array<string | null | undefined>,
  coreData: CoreDataModel
): string {
  const validCodes = codes.filter(Boolean);
  if (validCodes.length === 0) return "—";
  return validCodes
    .map((code) => lookupText(coreData.raceList, code))
    .join(", ");
}

export function getIEPStatusText(
  code: string | null | undefined,
  coreData: CoreDataModel
): string {
  return lookupText(coreData.IEPorIFSPList, code);
}

export function getContractTypeText(
  code: string | null | undefined,
  coreData: CoreDataModel
): string {
  return lookupText(coreData.contractTypesOfferedList, code);
}

export function getProgramTypeText(
  code: string | null | undefined,
  coreData: CoreDataModel
): string {
  return lookupText(coreData.programTypeList, code);
}

export function getEligibilityStatusText(
  code: string | null | undefined,
  coreData: CoreDataModel
): string {
  return lookupText(coreData.eligibilityStatusList, code);
}

export function getServiceReasonsText(
  fami: FAMI,
  hohPrefix: "HeadOfHousehold1" | "HeadOfHousehold2",
  coreData: CoreDataModel
): string {
  const codes: string[] =
    hohPrefix === "HeadOfHousehold1"
      ? [
          fami.HeadOfHousehold1NeedForService1,
          fami.HeadOfHousehold1NeedForService2,
          fami.HeadOfHousehold1NeedForService3,
          fami.HeadOfHousehold1NeedForService4,
          fami.HeadOfHousehold1NeedForService5,
          fami.HeadOfHousehold1NeedForService6,
          fami.HeadOfHousehold1NeedForService7,
          fami.HeadOfHousehold1NeedForService8,
          fami.HeadOfHousehold1NeedForService9,
        ].filter(Boolean)
      : [
          fami.HeadOfHousehold2NeedForService1,
          fami.HeadOfHousehold2NeedForService2,
          fami.HeadOfHousehold2NeedForService3,
          fami.HeadOfHousehold2NeedForService4,
          fami.HeadOfHousehold2NeedForService5,
          fami.HeadOfHousehold2NeedForService6,
          fami.HeadOfHousehold2NeedForService7,
          fami.HeadOfHousehold2NeedForService8,
          fami.HeadOfHousehold2NeedForService9,
        ].filter(Boolean);

  if (codes.length === 0) return "—";
  return codes
    .map((code) => lookupText(coreData.reasonForServiceList, code))
    .join(", ");
}
